import { useState } from "react";
import toast from "react-hot-toast";
import { validateShareableMessage } from "../../utils/shareChatMessage";

export default function ShareMessageModal({ open, onClose, message, contacts = [], onShareMessage }) {
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const handleContactToggle = (contactId) => {
        if (selectedContacts.includes(contactId)) {
            setSelectedContacts(selectedContacts.filter((id) => id !== contactId));
        } else if (selectedContacts.length < 5) {
            setSelectedContacts([...selectedContacts, contactId]);
        } else {
            toast.error("Maximum 5 contacts allowed");
        }
    };

    const handleShare = async () => {
        const validationError = validateShareableMessage(message);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        if (selectedContacts.length === 0) {
            toast.error("Select at least one contact");
            return;
        }

        if (typeof onShareMessage !== "function") {
            toast.error("Share action is not available");
            return;
        }

        setLoading(true);
        try {
            await onShareMessage(message, selectedContacts, contacts);
            toast.success(`Shared with ${selectedContacts.length} contact${selectedContacts.length > 1 ? "s" : ""}`);
            handleClose();
        } catch (err) {
            toast.error(err?.message || "Failed to share message");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedContacts([]);
        setSearch("");
        onClose();
    };

    if (!open) return null;

    const filteredContacts = contacts.filter(
        (c) =>
            (c.full_name || c.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (c.phone_number || "").includes(search)
    );

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-2" onClick={handleClose}>
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-[420px] max-h-[85vh] overflow-hidden relative flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white pb-3 z-20 pt-6 px-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Share Message</h2>
                        <button onClick={handleClose} aria-label="Close" className="p-2 rounded-full hover:bg-gray-100">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 8.586L15.95 2.636a1 1 0 111.414 1.414L11.414 10l5.95 5.95a1 1 0 01-1.414 1.414L10 11.414l-5.95 5.95A1 1 0 012.636 15.95L8.586 10 2.636 4.05A1 1 0 014.05 2.636L10 8.586z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="relative w-full mb-1">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                            <img src="../chatbox/magnifer.svg" alt="magnifer" className="w-5 h-5" />
                        </div>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search"
                            className="w-full rounded-[20px] border border-gray-300 bg-white pl-12 pr-4 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-md shadow-[#7C4DFF]/20 [appearance:textfield] [&::-webkit-search-cancel-button]:hidden"
                        />
                        {search && (
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                            >
                                <img src="../chatbox/close.svg" alt="close" className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    <p className="text-xs text-gray-500">Select up to 5 contacts to share this message</p>
                </div>

                <div className="space-y-3 pb-4 px-6 flex-1 overflow-y-auto max-h-[45vh] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
                    {filteredContacts.length > 0 ? (
                        filteredContacts.map((contact) => {
                            const isSelected = selectedContacts.includes(contact.user_id);
                            const profileImg = contact?.profile_image
                                ? `${process.env.REACT_APP_API_BASE_URL}${contact.profile_image}`
                                : "/profile-picture.png";
                            return (
                                <div
                                    key={contact.user_id}
                                    onClick={() => handleContactToggle(contact.user_id)}
                                    className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer transition ${
                                        isSelected ? "bg-[#EDE7FF] border-[#7C4DFF]" : "bg-[#797C7B] bg-opacity-[11%]"
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={profileImg}
                                            onError={(e) => {
                                                e.target.src = "/profile-picture.png";
                                            }}
                                            alt={contact.full_name || contact.name}
                                            className="h-10 w-10 rounded-full object-cover"
                                        />
                                        <div className="min-w-0">
                                            <p className="font-semibold text-sm text-gray-900 truncate">
                                                {contact.full_name || contact.name || "--"}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{contact.phone_number || ""}</p>
                                        </div>
                                    </div>
                                    <div
                                        className={`w-6 h-6 flex items-center justify-center rounded-full border-2 ${
                                            isSelected ? "bg-[#7C4DFF] border-[#7C4DFF]" : "border-gray-400"
                                        }`}
                                    >
                                        {isSelected && <img src="../chatscreen/radiobutton.svg" alt="selected" />}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-10 text-gray-500">
                            {contacts.length === 0 ? (
                                <>
                                    <p className="font-semibold">No contacts available</p>
                                    <p className="text-xs">You don&apos;t have any contacts yet.</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold">No results</p>
                                    <p className="text-xs">No contacts match &quot;{search}&quot;.</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 z-20 flex gap-3">
                    <button
                        onClick={handleClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-[14px] text-gray-700 font-medium hover:bg-gray-50 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleShare}
                        disabled={loading || selectedContacts.length === 0}
                        className={`flex-1 px-4 py-2 rounded-[14px] font-medium text-white transition ${
                            loading || selectedContacts.length === 0
                                ? "bg-[#7C4DFF]/60 cursor-not-allowed"
                                : "bg-[#7C4DFF] hover:bg-[#6938EF]"
                        } submit-button`}
                    >
                        {loading ? "Sharing..." : `Share (${selectedContacts.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
}
